/**
 * The editor's form widgets. Each one renders the `.field` markup the rest of the app uses, carries
 * a `data-path` so a finding in the checks panel can scroll to it, and shows the schema's own
 * message for its path when the draft does not parse — the same message the server would return.
 */
import type { ComponentChildren } from "preact";
import { createContext } from "preact";
import { useContext, useEffect, useLayoutEffect, useRef, useState } from "preact/hooks";
import { Button } from "../../components/ui";
import { useT } from "../../i18n";

/** Schema messages keyed by dotted path, from `Scenario.safeParse` of the working copy. */
export const FieldErrors = createContext<Map<string, string>>(new Map());

export function useFieldError(path: string): string | null {
  return useContext(FieldErrors).get(path) ?? null;
}

/** A DOM id from a scenario path; paths are unique per field, so ids are stable across renders. */
export function fid(path: string): string {
  return `f-${path.replace(/[^a-zA-Z0-9_-]/g, "_")}`;
}

export function BField({
  label,
  path,
  help,
  error,
  children,
  labelFor = true,
}: {
  label: string;
  path: string;
  help?: string | null;
  error?: string | null;
  children: ComponentChildren;
  /** False when the control labels itself (a group of checkboxes, a segmented control). */
  labelFor?: boolean;
}) {
  const schemaError = useFieldError(path);
  const shown = error ?? schemaError;
  const id = fid(path);
  return (
    <div class="field" data-path={path}>
      {labelFor ? <label for={id}>{label}</label> : <div class="label">{label}</div>}
      {children}
      {help ? (
        <div class="help" id={`${id}-help`}>
          {help}
        </div>
      ) : null}
      {shown ? (
        <div class="error-text" id={`${id}-err`}>
          {shown}
        </div>
      ) : null}
    </div>
  );
}

function describedBy(path: string, help?: string | null, error?: string | null): string | undefined {
  const id = fid(path);
  const parts = [help ? `${id}-help` : "", error ? `${id}-err` : ""].filter(Boolean);
  return parts.length ? parts.join(" ") : undefined;
}

export function TextField({
  label,
  path,
  value,
  onChange,
  help,
  placeholder,
  mono,
  type = "text",
}: {
  label: string;
  path: string;
  value: string;
  onChange: (v: string) => void;
  help?: string | null;
  placeholder?: string;
  mono?: boolean;
  type?: "text" | "url";
}) {
  const error = useFieldError(path);
  return (
    <BField label={label} path={path} help={help}>
      <input
        id={fid(path)}
        class={`input${mono ? " build-mono" : ""}${error ? " error" : ""}`}
        type={type}
        dir="auto"
        value={value}
        placeholder={placeholder}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(path, help, error)}
        onInput={(e) => onChange((e.currentTarget as HTMLInputElement).value)}
      />
    </BField>
  );
}

/** A textarea that grows with its content, so a long body never hides behind an inner scrollbar. */
export function TextArea({
  label,
  path,
  value,
  onChange,
  help,
  rows = 3,
}: {
  label: string;
  path: string;
  value: string;
  onChange: (v: string) => void;
  help?: string | null;
  rows?: number;
}) {
  const error = useFieldError(path);
  const ref = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight + 2}px`;
  }, [value]);
  return (
    <BField label={label} path={path} help={help}>
      <textarea
        ref={ref}
        id={fid(path)}
        class={`input build-textarea${error ? " error" : ""}`}
        rows={rows}
        dir="auto"
        value={value}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(path, help, error)}
        onInput={(e) => onChange((e.currentTarget as HTMLTextAreaElement).value)}
      />
    </BField>
  );
}

/**
 * A number the author may be half-way through typing: the text is kept locally and only a finite
 * number is committed, so "-" on the way to "-5" never becomes NaN in the draft.
 */
export function NumberField({
  label,
  path,
  value,
  onChange,
  help,
  min,
  max,
  step = 1,
  optional,
  placeholder,
  compact,
}: {
  label: string;
  path: string;
  value: number | undefined;
  onChange: (v: number | undefined) => void;
  help?: string | null;
  min?: number;
  max?: number;
  step?: number;
  optional?: boolean;
  placeholder?: string;
  compact?: boolean;
}) {
  const t = useT();
  const [text, setText] = useState(value === undefined ? "" : String(value));
  const [local, setLocal] = useState<string | null>(null);
  useEffect(() => {
    setText((cur) =>
      cur.trim() !== "" && Number(cur) === value ? cur : value === undefined ? "" : String(value),
    );
  }, [value]);
  const schemaError = useFieldError(path);
  const error = local ?? schemaError;
  const input = (
    <input
      id={fid(path)}
      class={`input${error ? " error" : ""}`}
      type="number"
      inputMode="decimal"
      min={min}
      max={max}
      step={step}
      value={text}
      placeholder={placeholder}
      aria-label={compact ? label : undefined}
      aria-invalid={error ? true : undefined}
      aria-describedby={describedBy(path, help, error)}
      onInput={(e) => {
        const v = (e.currentTarget as HTMLInputElement).value;
        setText(v);
        if (v.trim() === "") {
          if (optional) {
            setLocal(null);
            onChange(undefined);
          } else setLocal(t("build.field.numberRequired"));
          return;
        }
        const n = Number(v);
        if (!Number.isFinite(n)) {
          setLocal(t("build.field.numberRequired"));
          return;
        }
        setLocal(null);
        onChange(n);
      }}
    />
  );
  if (compact) return input;
  return (
    <BField label={label} path={path} help={help} error={local}>
      {input}
    </BField>
  );
}

export function SelectField<T extends string>({
  label,
  path,
  value,
  options,
  onChange,
  help,
}: {
  label: string;
  path: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
  help?: string | null;
}) {
  const error = useFieldError(path);
  const known = options.some((o) => o.value === value);
  return (
    <BField label={label} path={path} help={help}>
      <select
        id={fid(path)}
        class={`input${error || !known ? " error" : ""}`}
        value={value}
        aria-invalid={error || !known ? true : undefined}
        aria-describedby={describedBy(path, help, error)}
        onChange={(e) => onChange((e.currentTarget as HTMLSelectElement).value as T)}
      >
        {known ? null : <option value={value}>{value || "—"}</option>}
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </BField>
  );
}

/** Up, down and remove for one row of an ordered list. */
export function RowTools({
  label,
  index,
  count,
  onMove,
  onRemove,
  min = 0,
}: {
  /** Names the row for screen readers, e.g. "Option 2". */
  label: string;
  index: number;
  count: number;
  onMove: (delta: number) => void;
  onRemove: () => void;
  min?: number;
}) {
  const t = useT();
  return (
    <div class="build-rowtools">
      <Button
        variant="ghost"
        size="sm"
        icon="chevron-down"
        class="build-flip"
        aria-label={t("build.list.up", { item: label })}
        title={t("build.list.up", { item: label })}
        disabled={index === 0}
        onClick={() => onMove(-1)}
      />
      <Button
        variant="ghost"
        size="sm"
        icon="chevron-down"
        aria-label={t("build.list.down", { item: label })}
        title={t("build.list.down", { item: label })}
        disabled={index === count - 1}
        onClick={() => onMove(1)}
      />
      <Button
        variant="ghost"
        size="sm"
        icon="trash"
        aria-label={t("build.list.remove", { item: label })}
        title={t("build.list.remove", { item: label })}
        disabled={count <= min}
        onClick={onRemove}
      />
    </div>
  );
}

/** An ordered list of short strings: objectives, red flags, lessons, tags. */
export function StringList({
  label,
  path,
  items,
  onChange,
  help,
  itemLabel,
  multiline,
}: {
  label: string;
  path: string;
  items: string[];
  onChange: (items: string[]) => void;
  help?: string | null;
  /** The name of one entry, for the row's accessible name. */
  itemLabel: string;
  multiline?: boolean;
}) {
  const t = useT();
  const listRef = useRef<HTMLOListElement>(null);
  const [focusLast, setFocusLast] = useState(false);
  useEffect(() => {
    if (!focusLast) return;
    const fields = listRef.current?.querySelectorAll<HTMLElement>("input, textarea");
    fields?.[fields.length - 1]?.focus();
    setFocusLast(false);
  }, [focusLast]);
  const set = (i: number, v: string) => onChange(items.map((x, j) => (j === i ? v : x)));
  return (
    <BField label={label} path={path} help={help} labelFor={false}>
      {items.length ? (
        <ol class="build-list" ref={listRef}>
          {items.map((item, i) => {
            const rowLabel = `${itemLabel} ${i + 1}`;
            return (
              <li key={i} class="build-list-row" data-path={`${path}.${i}`}>
                {multiline ? (
                  <textarea
                    class="input build-textarea"
                    rows={2}
                    dir="auto"
                    value={item}
                    aria-label={rowLabel}
                    onInput={(e) => set(i, (e.currentTarget as HTMLTextAreaElement).value)}
                  />
                ) : (
                  <input
                    class="input"
                    dir="auto"
                    value={item}
                    aria-label={rowLabel}
                    onInput={(e) => set(i, (e.currentTarget as HTMLInputElement).value)}
                  />
                )}
                <RowTools
                  label={rowLabel}
                  index={i}
                  count={items.length}
                  onMove={(d) => {
                    const next = [...items];
                    const [x] = next.splice(i, 1);
                    next.splice(i + d, 0, x ?? "");
                    onChange(next);
                  }}
                  onRemove={() => onChange(items.filter((_, j) => j !== i))}
                />
              </li>
            );
          })}
        </ol>
      ) : (
        <p class="muted small build-empty-line">{t("build.list.none")}</p>
      )}
      <div>
        <Button
          variant="secondary"
          size="sm"
          icon="plus"
          onClick={() => {
            onChange([...items, ""]);
            setFocusLast(true);
          }}
        >
          {t("build.list.add", { item: itemLabel })}
        </Button>
      </div>
    </BField>
  );
}

/**
 * An id other parts of the scenario point at. It commits on blur or Enter, never per keystroke, so
 * typing "ver" on the way to "verify" does not collide with an existing "ver" or rewrite every
 * reference three times. Escape puts the old id back.
 */
export function IdField({
  label,
  path,
  value,
  taken,
  pattern,
  refs,
  onRename,
  help,
}: {
  label: string;
  path: string;
  value: string;
  taken: string[];
  pattern: RegExp;
  /** How many references a rename would update; omitted for ids nothing points at. */
  refs?: number;
  onRename: (to: string) => void;
  help?: string | null;
}) {
  const t = useT();
  const [text, setText] = useState(value);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setText(value);
    setError(null);
  }, [value]);
  const commit = () => {
    const next = text.trim();
    if (next === value) {
      setError(null);
      return;
    }
    if (!pattern.test(next)) {
      setError(t(pattern.source.includes("A-Z") ? "build.id.invalidVar" : "build.id.invalid"));
      return;
    }
    if (taken.includes(next)) {
      setError(t("build.id.taken", { id: next }));
      return;
    }
    setError(null);
    onRename(next);
  };
  const note =
    refs === undefined ? help : [t("build.id.refs", { count: refs }), help].filter(Boolean).join(" ");
  return (
    <BField label={label} path={path} help={note} error={error}>
      <input
        id={fid(path)}
        class={`input build-mono${error ? " error" : ""}`}
        dir="ltr"
        value={text}
        spellcheck={false}
        autocomplete="off"
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(path, note, error)}
        onInput={(e) => setText((e.currentTarget as HTMLInputElement).value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commit();
          } else if (e.key === "Escape") {
            setText(value);
            setError(null);
          }
        }}
      />
    </BField>
  );
}

/**
 * Numbers over a known set of keys — points per dimension, deltas per variable. An empty box means
 * the key is absent (0), which keeps untouched dimensions out of the score as the docs advise. Keys
 * that no longer exist (a deleted dimension) are listed so the author can clear them.
 */
export function KeyNumberMap({
  label,
  path,
  keys,
  values,
  onChange,
  help,
  emptyText,
}: {
  label: string;
  path: string;
  keys: { id: string; label: string }[];
  values: Record<string, number>;
  onChange: (values: Record<string, number>) => void;
  help?: string | null;
  emptyText: string;
}) {
  const t = useT();
  const unknown = Object.keys(values).filter((k) => !keys.some((x) => x.id === k));
  const set = (k: string, v: number | undefined) => {
    const next = { ...values };
    if (v === undefined || v === 0) delete next[k];
    else next[k] = v;
    onChange(next);
  };
  return (
    <BField label={label} path={path} help={help} labelFor={false}>
      {keys.length || unknown.length ? (
        <div class="build-kv">
          {keys.map((k) => (
            <div key={k.id} class="build-kv-item">
              <label class="build-kv-label" for={fid(`${path}.${k.id}`)} title={k.id}>
                {k.label}
              </label>
              <NumberField
                compact
                optional
                label={k.label}
                path={`${path}.${k.id}`}
                value={values[k.id]}
                placeholder="0"
                onChange={(v) => set(k.id, v)}
              />
            </div>
          ))}
          {unknown.map((k) => (
            <div key={k} class="build-kv-item unknown">
              <span class="build-kv-label error-text">{t("build.kv.unknown", { id: k })}</span>
              <Button variant="secondary" size="sm" icon="trash" onClick={() => set(k, undefined)}>
                {t("build.kv.clear")}
              </Button>
            </div>
          ))}
        </div>
      ) : (
        <p class="muted small build-empty-line">{emptyText}</p>
      )}
    </BField>
  );
}

/** A set of checkboxes over role ids (voters, role bonus). */
export function RoleChecks({
  label,
  path,
  roles,
  value,
  onChange,
  help,
}: {
  label: string;
  path: string;
  roles: { id: string; name: string }[];
  value: string[];
  onChange: (v: string[]) => void;
  help?: string | null;
}) {
  const unknown = value.filter((r) => !roles.some((x) => x.id === r));
  return (
    <BField label={label} path={path} help={help} labelFor={false}>
      <div class="build-checks" role="group" aria-label={label}>
        {[...roles, ...unknown.map((id) => ({ id, name: id }))].map((r) => (
          <label key={r.id} class={`build-check${unknown.includes(r.id) ? " unknown" : ""}`}>
            <input
              type="checkbox"
              checked={value.includes(r.id)}
              onChange={(e) => {
                const on = (e.currentTarget as HTMLInputElement).checked;
                onChange(on ? [...value, r.id] : value.filter((x) => x !== r.id));
              }}
            />
            <span>{r.name}</span>
          </label>
        ))}
      </div>
    </BField>
  );
}

/** A titled group of fields inside a form: a hairline and a small heading, as the console's cards. */
export function FormGroup({
  title,
  children,
  actions,
  path,
}: {
  title: string;
  children: ComponentChildren;
  actions?: ComponentChildren;
  path?: string;
}) {
  return (
    <section class="build-group" data-path={path}>
      <div class="build-group-head">
        <h3>{title}</h3>
        {actions ? <div class="row">{actions}</div> : null}
      </div>
      <div class="stack">{children}</div>
    </section>
  );
}
